/* Global Creachin' map (theme/global-creachin in the HBI repo).
   Loads Google Maps only when the section scrolls into view (and, where the cookie banner
   applies, once preferences are allowed or the visitor clicks to show it), then plots one
   marker per town or city from assets/global-creachin-places.json and clusters them.
   Map styles are Snazzy Maps JSON: Old Map (111158), Pale Dawn (1), Cream (79601). */
(() => {
  const STYLES = {"old_map":[{"featureType":"all","elementType":"labels","stylers":[{"visibility":"simplified"}]},{"featureType":"administrative.country","elementType":"geometry.stroke","stylers":[{"color":"#b1917b"},{"weight":"0.76"}]},{"featureType":"administrative.country","elementType":"labels","stylers":[{"visibility":"off"}]},{"featureType":"administrative.province","elementType":"all","stylers":[{"visibility":"off"}]},{"featureType":"administrative.locality","elementType":"all","stylers":[{"visibility":"off"}]},{"featureType":"landscape","elementType":"all","stylers":[{"color":"#ece2cc"}]},{"featureType":"landscape","elementType":"labels","stylers":[{"visibility":"off"}]},{"featureType":"landscape.natural.terrain","elementType":"all","stylers":[{"color":"#c6b595"}]},{"featureType":"landscape.natural.terrain","elementType":"geometry.stroke","stylers":[{"color":"#ff0000"}]},{"featureType":"poi","elementType":"all","stylers":[{"visibility":"off"}]},{"featureType":"road","elementType":"all","stylers":[{"visibility":"off"}]},{"featureType":"transit","elementType":"all","stylers":[{"visibility":"off"}]},{"featureType":"water","elementType":"all","stylers":[{"color":"#b8a386"}]},{"featureType":"water","elementType":"geometry.fill","stylers":[{"color":"#23686d"}]},{"featureType":"water","elementType":"labels","stylers":[{"color":"#ece2cc"}]}],"pale_dawn":[{"featureType":"administrative","elementType":"all","stylers":[{"visibility":"on"},{"lightness":33}]},{"featureType":"landscape","elementType":"all","stylers":[{"color":"#f2e5d4"}]},{"featureType":"poi.park","elementType":"geometry","stylers":[{"color":"#c5dac6"}]},{"featureType":"poi.park","elementType":"labels","stylers":[{"visibility":"on"},{"lightness":20}]},{"featureType":"road","elementType":"all","stylers":[{"lightness":20}]},{"featureType":"road.highway","elementType":"geometry","stylers":[{"color":"#c5c6c6"}]},{"featureType":"road.arterial","elementType":"geometry","stylers":[{"color":"#e4d7c6"}]},{"featureType":"road.local","elementType":"geometry","stylers":[{"color":"#fbfaf7"}]},{"featureType":"water","elementType":"all","stylers":[{"visibility":"on"},{"color":"#acbcc9"}]}],"cream":[{"featureType":"all","elementType":"labels.text.fill","stylers":[{"saturation":"0"},{"color":"#a1a195"},{"lightness":"0"}]},{"featureType":"all","elementType":"labels.text.stroke","stylers":[{"visibility":"on"},{"color":"#fefef8"},{"lightness":"0"},{"gamma":"1"}]},{"featureType":"all","elementType":"labels.icon","stylers":[{"visibility":"off"}]},{"featureType":"administrative","elementType":"geometry.fill","stylers":[{"lightness":20},{"color":"#ebebdf"}]},{"featureType":"administrative","elementType":"geometry.stroke","stylers":[{"lightness":17},{"weight":1.2}]},{"featureType":"administrative.country","elementType":"labels.text.fill","stylers":[{"color":"#808077"},{"saturation":"0"}]},{"featureType":"administrative.province","elementType":"labels.text.fill","stylers":[{"color":"#808077"}]},{"featureType":"administrative.locality","elementType":"labels.text.fill","stylers":[{"color":"#808077"}]},{"featureType":"landscape","elementType":"geometry","stylers":[{"color":"#f4f4e8"},{"lightness":"0"},{"gamma":"1"}]},{"featureType":"poi","elementType":"geometry","stylers":[{"color":"#ebebdf"},{"lightness":"0"},{"gamma":"1.00"}]},{"featureType":"road","elementType":"geometry.fill","stylers":[{"color":"#fefef8"}]},{"featureType":"road.highway","elementType":"geometry.stroke","stylers":[{"lightness":29},{"weight":0.2},{"visibility":"off"}]},{"featureType":"transit","elementType":"geometry","stylers":[{"color":"#ebebdf"}]},{"featureType":"water","elementType":"geometry","stylers":[{"color":"#deded3"},{"lightness":17}]}]};
  const CLUSTERER_SRC = 'https://unpkg.com/@googlemaps/markerclusterer@2.5.3/dist/index.min.js';

  const loadScript = (src) => new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = src; s.async = true; s.onload = resolve; s.onerror = reject;
    document.head.appendChild(s);
  });

  let mapsPromise;
  const loadMaps = (key) => {
    if (window.google?.maps?.Map) return Promise.resolve();
    if (!mapsPromise) {
      mapsPromise = new Promise((resolve, reject) => {
        window.__globalCreachinMapsReady = resolve;
        loadScript(`https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(key)}&callback=__globalCreachinMapsReady&loading=async&v=weekly`).catch(reject);
      });
    }
    return mapsPromise;
  };

  const dot = (fill, ring, size) =>
    'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(
      `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">` +
      `<circle cx="${size / 2}" cy="${size / 2}" r="${size / 2 - 1.5}" fill="${fill}" fill-opacity="0.9" stroke="${ring}" stroke-width="2"/></svg>`);

  async function build(root) {
    const cfg = root.dataset;
    const canvas = root.querySelector('[data-gc-canvas]');
    const [, data] = await Promise.all([loadMaps(cfg.key), fetch(cfg.data).then((r) => r.json())]);
    await loadScript(CLUSTERER_SRC);

    let style = cfg.style === 'custom' ? JSON.parse(root.querySelector('[data-gc-custom-style]')?.textContent || '[]') : STYLES[cfg.style] || STYLES.old_map;
    // Country names on top of any style (later rules win), in the shop's dark ink on a cream halo.
    if (cfg.countries !== 'false') style = style.concat([
      { featureType: 'administrative.country', elementType: 'labels', stylers: [{ visibility: 'on' }] },
      { featureType: 'administrative.country', elementType: 'labels.text.fill', stylers: [{ color: cfg.labelColor || '#3b3127' }] },
      { featureType: 'administrative.country', elementType: 'labels.text.stroke', stylers: [{ color: '#f5f1e4' }, { weight: 3 }] },
    ]);
    // One copy of the world, filling the frame: the smallest zoom at which the world (256px at
    // zoom 0) is at least as wide and as tall as the frame, and no panning past its edges.
    // Detail for closer zooms: firmer country borders, faint region borders, town names and a
    // whisper of major roads, so a zoomed-in view is not just cream with dots on it.
    if (cfg.detail !== 'false') {
      const ink = cfg.labelColor || '#3b3127';
      // Snazzy styles often switch off all points of interest, simplify all labels and hide towns;
      // the first two also take Google's town names with them (found by testing Old Map). Drop
      // those rules and switch the point of interest types off one by one instead. (Switching the
      // town label icons off also hid the names, so Google's small town dots stay.)
      const off = (r) => (r.stylers || []).some((x) => x.visibility === 'off' || x.visibility === 'simplified');
      const ft = (r) => r.featureType || 'all';
      const et = (r) => r.elementType || 'all';
      style = style.filter((r) => !(
        (ft(r) === 'all' && et(r) === 'labels' && off(r)) ||
        (ft(r) === 'poi' && et(r) === 'all' && off(r)) ||
        (ft(r) === 'administrative.locality' && off(r))
      ));
      style = style.concat(['attraction', 'business', 'government', 'medical', 'park', 'place_of_worship', 'school', 'sports_complex']
        .map((t) => ({ featureType: `poi.${t}`, elementType: 'all', stylers: [{ visibility: 'off' }] })));
      style = style.concat([
        { featureType: 'administrative.country', elementType: 'geometry.stroke', stylers: [{ visibility: 'on' }, { color: '#7d6a58' }, { weight: 1.3 }] },
        { featureType: 'administrative.province', elementType: 'geometry.stroke', stylers: [{ visibility: 'on' }, { color: '#bfae92' }, { weight: 0.7 }] },
        { featureType: 'administrative.locality', elementType: 'labels.text.fill', stylers: [{ color: ink }] },
        { featureType: 'administrative.locality', elementType: 'labels.text.stroke', stylers: [{ color: '#f5f1e4' }, { weight: 3 }] },
        { featureType: 'road.highway', elementType: 'geometry', stylers: [{ visibility: 'simplified' }, { color: '#dccdb0' }, { weight: 0.6 }] },
        { featureType: 'road.highway', elementType: 'labels', stylers: [{ visibility: 'off' }] },
      ]);
    }
    // The smallest whole zoom at which one copy of the world fills the frame (the raster tiles
    // these JSON styles use do not do fractional zoom), opening on North America and Europe on
    // desktop and on the UK on narrow screens, where most of the markers are.
    const fit = Math.log2(Math.max(canvas.clientWidth, canvas.clientHeight * 1.6) / 256);
    const minZoom = Math.max(1, Math.ceil(fit));
    const narrow = canvas.clientWidth < 750;
    const map = new google.maps.Map(canvas, {
      center: narrow ? { lat: 50, lng: -3 } : { lat: 36, lng: -25 }, zoom: minZoom, minZoom, maxZoom: 10,
      restriction: { latLngBounds: { north: 80, south: -60, west: -180, east: 180 }, strictBounds: true },
      styles: style, disableDefaultUI: true, zoomControl: true, gestureHandling: 'cooperative',
      backgroundColor: cfg.background || '#f5f1e4', clickableIcons: false,
    });

    const markerIcon = { url: dot(cfg.marker, cfg.ring, 14), anchor: new google.maps.Point(7, 7), scaledSize: new google.maps.Size(14, 14) };
    // Town name on hover (desktop) and on click or tap (touch): "Glasgow, United Kingdom".
    const regions = typeof Intl.DisplayNames === 'function' ? new Intl.DisplayNames([document.documentElement.lang || 'en'], { type: 'region' }) : null;
    const label = (name, cc) => { let c = cc; try { c = regions?.of(cc) || cc; } catch (e) {} return `${name}, ${c}`; };
    // Our own small label rather than Google's InfoWindow (whose frame clashes with the theme).
    class Tip extends google.maps.OverlayView {
      constructor() { super(); this.div = document.createElement('div'); this.div.className = 'global-creachin__tip'; this.pos = null; }
      onAdd() { this.getPanes().floatPane.appendChild(this.div); }
      draw() {
        if (!this.pos) { this.div.style.display = 'none'; return; }
        const p = this.getProjection().fromLatLngToDivPixel(this.pos);
        this.div.style.left = `${p.x}px`; this.div.style.top = `${p.y}px`; this.div.style.display = 'block';
      }
      onRemove() { this.div.remove(); }
      show(pos, text) { this.pos = pos; this.div.textContent = text; this.draw(); }
      hide() { this.pos = null; this.draw(); }
    }
    const tip = new Tip();
    tip.setMap(map);
    let pinned = null;
    const open = (m, pin) => { tip.show(m.getPosition(), m.get('gcLabel')); pinned = pin ? m : null; };
    const close = () => { tip.hide(); pinned = null; };
    const markers = data.cities.map(([lat, lng, name, cc]) => {
      const m = new google.maps.Marker({ position: { lat, lng }, icon: markerIcon, optimized: true });
      m.set('gcLabel', label(name, cc));
      m.addListener('mouseover', () => { if (!pinned) open(m, false); });
      m.addListener('mouseout', () => { if (!pinned) tip.hide(); });
      m.addListener('click', () => (pinned === m ? close() : open(m, true)));
      return m;
    });
    map.addListener('click', close);
    map.addListener('zoom_changed', close);

    const showCounts = cfg.counts === 'true';
    const renderer = {
      render: ({ count, position }) => {
        const size = Math.round(Math.min(56, 20 + Math.log2(count) * 6));
        return new google.maps.Marker({
          position, zIndex: 1000 + count,
          icon: { url: dot(cfg.cluster, cfg.ring, size), anchor: new google.maps.Point(size / 2, size / 2), scaledSize: new google.maps.Size(size, size) },
          label: showCounts ? { text: String(count), color: cfg.clusterText || '#f5f1e4', fontSize: '12px', fontWeight: '600' } : null,
        });
      },
    };
    new markerClusterer.MarkerClusterer({ map, markers, renderer });

    const stats = root.querySelector('[data-gc-stats]');
    if (stats) stats.textContent = stats.dataset.template
      .replace('[countries]', data.countries.toLocaleString())
      .replace('[places]', data.cities.length.toLocaleString());
    root.gcMap = map; // handy for checking in the console
    root.gcMarkers = markers;
    root.classList.add('is-loaded');
  }

  // Shopify's cookie banner: the privacy API is not on the page by default, so load it first.
  // Where the banner applies and preferences are not yet allowed, show a click-to-load notice,
  // and load the map anyway as soon as the visitor accepts the banner.
  const privacyApi = () => new Promise((resolve) => {
    if (window.Shopify?.customerPrivacy) return resolve(window.Shopify.customerPrivacy);
    if (typeof window.Shopify?.loadFeatures !== 'function') return resolve(null);
    window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (err) => resolve(err ? null : window.Shopify.customerPrivacy || null));
  });

  async function start(root) {
    let started = false;
    const go = () => {
      if (started) return;
      started = true;
      gate && (gate.hidden = true);
      build(root).catch((e) => { root.classList.add('has-error'); console.warn('[global-creachin]', e); });
    };
    const gate = root.querySelector('[data-gc-consent]');
    const cp = await privacyApi();
    const blocked = cp && typeof cp.shouldShowBanner === 'function' && cp.shouldShowBanner() && !cp.preferencesProcessingAllowed();
    if (!blocked || !gate) return go();
    gate.hidden = false;
    gate.querySelector('button')?.addEventListener('click', go, { once: true });
    document.addEventListener('visitorConsentCollected', (e) => { if (e.detail?.preferencesAllowed) go(); });
  }

  function init(root) {
    if (root.dataset.gcInit) return;
    root.dataset.gcInit = '1';
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) { io.disconnect(); start(root); }
    }, { rootMargin: '300px' });
    io.observe(root);
  }

  const scan = () => document.querySelectorAll('[data-global-creachin]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', scan); else scan();
  document.addEventListener('shopify:section:load', scan);
})();
